import {
    Component,
    Element,
    h,
    State,
    Listen,
    forceUpdate,
    Prop,
} from "@stencil/core";
import { getViewportWidth } from "../../../utils/getViewportWidth";
import { debounce } from "../../../utils/debounce";

import {
    ContentDataProvider,
    ContentDataSchema,
    getContentDataProvider,
} from "../../../apis/stateful-content";
import { TableCell } from "./table-cell";
import { TableHeader } from "./table-header";
import { ColumnTypes, InternalColumnTypes } from "./column-types";
import { renderTextAndDate } from "./cells/textAndDate";

import {
    getTranslateFn,
    TranslateFn,
    getTranslationSource,
    TranslationRenderType,
} from "../stateful-content-utils";

@Component({
    tag: "ux-iws-stateful-table",
    styleUrl: "stateful-table.scss",
    shadow: false,
})
export class UxIwsStatefulTable {
    private dataProvider: ContentDataProvider | undefined;
    private translate: TranslateFn;
    private translationRenderType: TranslationRenderType =
        TranslationRenderType.Content;
    private dataProviderUnsubscribe: () => void;

    @Element() domNode: HTMLUxIwsStatefulTableElement;

    /** The scope of the component.*/
    @Prop() scope: string = "";

    /** The URL of the component. */
    @Prop() url: string = "";

    /** maximum number of pixels that the fixed column is allowed to be wide */
    @Prop() maxFixedColWidth: number;

    private sortStates: { [key: string]: "asc" | "desc" } = {};
    @State() isMobile: boolean = getViewportWidth() < 768;

    componentWillLoad(): void {
        this.setupDataProvider();
        this.setIsMobile();
        this.setTranslations();
    }

    disconnectedCallback() {
        this.dataProviderUnsubscribe?.();
    }

    @Listen("resize", { target: "window" })
    onResize(): void {
        this.setIsMobileDebounced();
    }

    /**
     * handle script tag translations as well as deprecated translations prop fallback
     **/
    private setTranslations = () => {
        this.translate = getTranslateFn(
            getTranslationSource(
                this.scope,
                this.domNode,
                this.translationRenderType
            )
        );
    };

    private setupDataProvider(): void {
        this.dataProvider = getContentDataProvider(this.scope);
        this.dataProviderUnsubscribe = this.dataProvider?.subscribe(
            this.handleDataProviderState
        );
        if (this.url !== "") {
            this.dataProvider?.setEndpoint(this.url);
        }

        const sortParam = this.dataProvider?.sort.split("-");
        if (
            sortParam &&
            sortParam.length === 2 &&
            (sortParam[1] === "asc" || sortParam[1] === "desc")
        ) {
            this.sortStates = {
                ...this.sortStates,
                [sortParam[0]]: sortParam[1] as "asc" | "desc",
            };
        }
    }

    private handleDataProviderState = (): void => {
        forceUpdate(this);
    };

    private setIsMobile = (): void => {
        const newIsMobile = getViewportWidth() < 768;
        if (this.isMobile !== newIsMobile) {
            this.isMobile = newIsMobile;
        }
    };

    private setIsMobileDebounced = debounce(() => {
        const newIsMobile = getViewportWidth() < 768;
        if (this.isMobile !== newIsMobile) {
            this.isMobile = newIsMobile;
        }
    }, 200);

    private handleSort = (key: string): void => {
        const sortDirection = this.sortStates[key] === "asc" ? "desc" : "asc";
        this.sortStates = {
            ...this.sortStates,
            [key]: sortDirection,
        };
        this.dataProvider?.setSortParam(`${key}-${sortDirection}`);
    };

    private getDynamicColumnOrder(schema: { [key: string]: any }): string[] {
        const primaryMobile = [];
        const others = [];

        for (const key in schema) {
            if (schema[key]["primary-mobile"]) {
                primaryMobile.push(key);
            } else {
                others.push(key);
            }
        }

        return this.isMobile
            ? [...primaryMobile, ...others]
            : Object.keys(schema);
    }

    private getPrimaryMobileItemKeys(
        schema: { [key: string]: any },
        type: ColumnTypes
    ): string[] {
        if (!this.isMobile) {
            return [];
        }
        for (const key in schema) {
            if (schema[key]["primary-mobile"] && schema[key].type === type) {
                return [key];
            }
        }
        return [];
    }

    // this function transforms a few things so the table can display a date and text in the same column
    // to achieve this, you need to set the primary-mobile attribute on 1 text column and 1 date column
    private transformDataForDateAndText(
        schema: ContentDataSchema,
        originalColumnOrder: string[]
    ) {
        const primaryTextKeys = this.getPrimaryMobileItemKeys(
            schema,
            ColumnTypes.Text
        );
        const primaryDateKeys = this.getPrimaryMobileItemKeys(
            schema,
            ColumnTypes.Date
        );
        const dateAndTextSchemaItem =
            primaryTextKeys.length > 0 && primaryDateKeys.length > 0
                ? {
                      ...schema[primaryTextKeys[0]],
                      type: InternalColumnTypes.TextAndDate,
                      dateKey: primaryDateKeys[0],
                  }
                : null;
        const columnOrder = [
            ...primaryTextKeys,
            ...originalColumnOrder.filter((key) => key !== primaryTextKeys[0]),
        ];
        const isDateAndText = (key: string) =>
            primaryTextKeys.length > 0 &&
            key === primaryTextKeys[0] &&
            dateAndTextSchemaItem !== null;
        const headerSchema = {
            ...schema,
            ...{ [primaryTextKeys[0]]: dateAndTextSchemaItem },
        };
        return {
            dateAndTextSchemaItem,
            columnOrder,
            isDateAndText,
            headerSchema,
        };
    }

    render() {
        if (!this.dataProvider || !this.dataProvider.data) {
            return null;
        }
        const { schema = {}, items = [] } = this.dataProvider.data;
        const {
            dateAndTextSchemaItem,
            columnOrder,
            isDateAndText,
            headerSchema,
        } = this.transformDataForDateAndText(
            schema,
            this.getDynamicColumnOrder(schema)
        );

        return (
            <div>
                <ux-iws-sticky-scroll-bar></ux-iws-sticky-scroll-bar>
                <ux-table
                    full-width
                    max-fixed-col-width={this.maxFixedColWidth}
                    class="ux-iws-stateful-table__table"
                >
                    <table>
                        <TableHeader
                            schema={headerSchema}
                            sortedKeys={columnOrder}
                            handleSort={this.handleSort}
                            translate={this.translate}
                        />
                        <tbody>
                            {items.map((item) => (
                                <tr
                                    key={`${item.id || item.number}-${item.detailLink}-${item.checkboxValue}`}
                                    class={`${item.orderTracking ? "order-tracking" : ""}`}
                                >
                                    {columnOrder.map((key) => {
                                        if (isDateAndText(key)) {
                                            return renderTextAndDate({
                                                item,
                                                columnKey: key,
                                                schemaItem:
                                                    dateAndTextSchemaItem,
                                                translate: this.translate,
                                            });
                                        }
                                        return (
                                            <TableCell
                                                item={item}
                                                columnKey={key}
                                                schemaItem={schema[key]}
                                                translate={this.translate}
                                            />
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </ux-table>
            </div>
        );
    }
}
